import { useMemo } from 'react';

import { SHIP_OPTION_VALUES, TRANSFER_OPTION_VALUES } from '@/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { DonorSchedule } from '@/rtk-query.gql';

import { ValidationField } from './types';

export const CLINIC_RANCH_HAUL_IN_OPTIONS: { name: string; value: number }[] = [
  { name: 'Clinic', value: 1 },
  { name: 'Ranch', value: 2 },
  { name: 'Haul-In', value: 3 },
];

export const TARGET_TIME_OPTIONS = [
  { name: 'AM', value: false },
  { name: 'PM', value: true },
];

export const CLEARABLE_FIELDS = [
  { label: 'Account', value: 'ownerId' },
  { label: 'All Semen Related Fields', value: 'allSemenRelatedFields' },
  { label: 'Asp Site', value: 'aspirationSiteId' },
  { label: 'Aspiration Comment', value: 'specialInstructions' },
  { label: 'Backup Sire', value: 'backupSireId' },
  { label: 'Bill Drugs?', value: 'billDrugs' },
  { label: 'Billing Account', value: 'billingAccountId' },
  { label: 'Cane Code', value: 'cane' },
  { label: 'Clinic/Ranch/Haulin', value: 'clinicRanchHaulin' },
  { label: 'Col Code', value: 'colCode' },
  { label: 'CSR', value: 'csrId' },
  { label: 'Donor', value: 'donorId' },
  { label: 'DOW', value: 'dow' },
  { label: 'Export?', value: 'export' },
  { label: 'Fert Site', value: 'fertilizationSiteId' },
  { label: 'Freeze', value: 'freeze' },
  { label: 'Freeze Instructions', value: 'freezeInstructions' },
  { label: 'Freeze Instructions', value: 'freezeinstructions' },
  { label: 'FSH Drug 1 ', value: 'fshId' },
  { label: 'Implant Type', value: 'implantType' },
  { label: 'NAAB', value: 'naab' },
  { label: 'NEW Shipment Group', value: 'shipmentGroupId' },
  { label: 'Non-EU Country', value: 'nonEUCountry' },
  { label: 'Revision #', value: 'revision' },
  { label: 'Revision Comments', value: 'revisionComment' },
  { label: 'Schedule', value: 'scheduleMasterId' },
  { label: 'Schedule Sent?', value: 'scheduled' },
  { label: 'Semen', value: 'semenId' },
  { label: 'Semen Comment', value: 'semenComment' },
  { label: 'Semen Location', value: 'semenLocationId' },
  { label: 'Semen Owner', value: 'semenOwnerId' },
  { label: 'Semen Price', value: 'semenPrice' },
  { label: 'Semen Type', value: 'semenType' },
  { label: 'Ship?', value: 'ship' },
  { label: 'Shipment Group', value: 'shipmentGroupId' },
  { label: 'Sire', value: 'sireId' },
  { label: 'SO schedule', value: 'soScheduleId' },
  { label: 'Transfer?', value: 'transfer' },
  { label: 'Transfer Instructions', value: 'embryoComment' },
  { label: 'TT', value: 'tt' },
];

export const TOG_90_DAYS_PAST_DUE = {
  YES: 100000000,
  NO: 100000001,
  CLOSED: 100000002,
};

export const validationFields: ValidationField[] = [
  { field: 'date', label: 'Date', required: true },
  { field: 'fertilizationSiteId', label: 'Fertilization Site', required: true },
  { field: 'csrId', label: 'CSR', required: true },
  { field: 'donorId', label: 'Donor', required: true },

  { field: 'loadDonorPartners', label: 'Bill Partnership', required: true },
  {
    field: 'ship',
    label: 'Ship',
    required: true,
    condition: (schedule: DonorSchedule) => schedule.freeze !== 100000002,
  },
  {
    field: 'shipmentGroup',
    label: 'Shipment Group',
    required: true,
    condition: (schedule: DonorSchedule) => {
      if (schedule.freeze === 100000002) {
        return false;
      }
      if (schedule.ship === 100000000) {
        return true;
      }

      return true;
    },
  },
  {
    field: 'billingAccount',
    label: 'Billing Account',
    required: true,
    condition: (schedule: DonorSchedule) =>
      schedule.loadDonorPartners !== 100000001,
  },
  { field: 'export', label: 'Export', required: true },
  {
    field: 'freeze',
    label: 'Freeze',
    required: true,
    condition: (schedule: DonorSchedule) =>
      schedule.ship !== SHIP_OPTION_VALUES.SHIP_ALL &&
      schedule.transfer !== TRANSFER_OPTION_VALUES.TRANSFER_ALL,
  },
  {
    field: 'freezeMethodId',
    label: 'Freeze Method',
    required: true,
    condition: (schedule: DonorSchedule) =>
      schedule.ship !== SHIP_OPTION_VALUES.SHIP_ALL &&
      schedule.transfer !== TRANSFER_OPTION_VALUES.TRANSFER_ALL,
  },
  { field: 'tt', label: 'TT', required: true },
  { field: 'scheduleMasterId', label: 'Schedule', required: true },
  { field: 'fshId', label: 'FSH Drug 1', required: true },
  { field: 'sireId', label: 'Sire', required: true },
  { field: 'semenType', label: 'Semen Type', required: true },
  { field: 'colCode', label: 'Collection Code', required: true },
  { field: 'semenLocationId', label: 'Semen Location', required: true },
  { field: 'semenOwnerId', label: 'Semen Owner', required: true },
];

export const BILL_ACCOUNT_CANING_OPTIONS = [
  { name: 'No Changes', value: 100000000 },
  { name: 'Groups of Three', value: 100000001 },
  { name: 'Odds and Evens', value: 100000002 },
];

export const tabIndexMap: Record<string, number> = {
  general: 0,
  embryos: 1,
  schedules: 2,
  semen: 3,
  notes: 4,
};

export const tabNameMap: Record<number, string> = {
  0: 'general',
  1: 'embryos',
  2: 'schedules',
  3: 'semen',
  4: 'notes',
};

export const SEMEN_TYPE_OPTIONS = [
  { name: 'Conv', value: 100000000 },
  { name: 'SF F', value: 100000001 },
  { name: 'SF M', value: 100000002 },
  { name: 'RS F', value: 100000003 },
  { name: 'RS M', value: 100000004 },
];
export const useAllowedNullFields = () => {
  const isFreezeShipPersistenceEnabled = useFeatureFlag(
    'ds_freeze_ship_persistence',
  );
  const isTransferFieldClearEnabled = useFeatureFlag(
    'DS_Transfer_Field_Clear',
  );

  return useMemo(
    () => [
      'semenId',
      'semenType',
      'semenLocationId',
      'cane',
      'naab',
      'billAccountCaningOption',
      'sireId',
      ...(isTransferFieldClearEnabled ? ['transfer'] : []),
      ...(isFreezeShipPersistenceEnabled
        ? [
            'ship',
            'shipmentGroupId',
            'freeze',
            'freezeInstructions',
            'freezeMethodId',
            'embryoComment',
            'implantType',
          ]
        : []),
    ],
    [isFreezeShipPersistenceEnabled, isTransferFieldClearEnabled],
  );
};

// FSH Drug 1 default names used when the `DS_Default_SO` flag is enabled and the
// FSH drug is defaulted from the Schedule Master's `twiceConcentration` value.
export const FOLLTROPIN_DRUG_NAME = 'Folltropin';
export const FOLLTROPIN_2X_DRUG_NAME = 'Folltropin 2X';

// Schedule Master name that signals no stimulation protocol — FSH drugs must not
// be populated when this name is matched (case-insensitive).
export const NO_STIM_SCHEDULE_NAME = 'NO STIM';
